import { useEffect, useState } from "react";
import { LoadingContainer } from "../../../app/LoadingContainer";
import { useLocalization } from "@fluent/react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { selectSettings } from "../../settings/settingsSlice";
import { ErrorContainer } from "../../../app/ErrorContainer";
import { APIError } from "../../../app/appTypes";
import { enUS, plPL } from "@mui/x-data-grid/locales";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { Box, Chip, Paper, TableContainer, Tooltip, IconButton, Checkbox } from "@mui/material";
import { unwrapResult } from "@reduxjs/toolkit";
import { CompanyDepartmentDetails, deleteCompanyDepartment, loadCompanyDepartments } from "./companyDepartmentsSlice";
import { FlexDiv } from "../../../app/Utils";
import { AddFabButton } from "../../../app/components/AddFabButton";
import { AddEditCompanyDepartmentDialog } from "./dialogs/AddEditCompanyDepartmentDialog";
import { DepartmentNotificationTargetLabel } from "./components/DepartmentNotificationTargetLabel";
import { DepartmentCodeBadge } from "./components/DepartmentCodeBadge";
import EditIcon from "@mui/icons-material/Edit";
import GroupIcon from "@mui/icons-material/Group";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import { DeleteConfirmationDialog } from "../../utils/DeleteConfirmationDialog";
import { CompanyDepartmentMembersDialog } from "./dialogs/CompanyDepartmentMembersDialog";
import { CompanyDepartmentNotificationsDialog } from "./dialogs/CompanyDepartmentNotificationsDialog";


export const CompanyDepartmentsList = () => {
    const dispatch = useAppDispatch();

    const { l10n } = useLocalization();
    const { locale } = useAppSelector(selectSettings);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [loadingError, setError] = useState<APIError | undefined>(undefined);
    const [loadingErrorOccured, setLoadingErrorOccured] = useState<boolean>(false);
    const [departments, setDepartments] = useState<CompanyDepartmentDetails[]>([]);
    const [createDialogVisible, setCreateDialogVisible] = useState<boolean>(false);
    const [editDialogVisible, setEditDialogVisible] = useState<boolean>(false);
    const [selectedDepartment, setSelectedDepartment] = useState<CompanyDepartmentDetails | undefined>(undefined);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [departmentToDeleteId, setDepartmentToDeleteId] = useState<number | undefined>(undefined);
    const [membersDialogVisible, setMembersDialogVisible] = useState(false);
    const [selectedDepartmentForMembers, setSelectedDepartmentForMembers] = useState<CompanyDepartmentDetails | undefined>(undefined);
    const [selectedDepartmentForNotifications, setSelectedDepartmentForNotifications] = useState<CompanyDepartmentDetails | undefined>(undefined);

    const openCreateDialog = () => {
        setCreateDialogVisible(true);
    };

    const getCustomLocaleText = () => {
        const defaultEnLocaleText = enUS.components.MuiDataGrid.defaultProps.localeText;

        if (locale === "pl") {
            return plPL.components.MuiDataGrid.defaultProps.localeText;
        } else {
            return defaultEnLocaleText;
        }
    };

    const handleEditClick = (department: CompanyDepartmentDetails) => {
        setSelectedDepartment(department);
        setEditDialogVisible(true);
    };

    const handleMembersClick = (department: CompanyDepartmentDetails) => {
        setSelectedDepartmentForMembers(department);
        setMembersDialogVisible(true);
    };
    const handleHideMembersDialog = () => {
        setMembersDialogVisible(false);
        setSelectedDepartmentForMembers(undefined);
    };
    const handleDeleteClick = (id: number | string) => {
        setDepartmentToDeleteId(Number(id));
        setDeleteDialogOpen(true);
    };
    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setDepartmentToDeleteId(undefined);
    };
    const handleDeleteConfirm = async () => {
        if (!departmentToDeleteId) {
            return;
        }

        const resultAction = await dispatch(
            deleteCompanyDepartment({
                id: departmentToDeleteId,
                hardDelete: true
            })
        );

        if (deleteCompanyDepartment.fulfilled.match(resultAction)) {
            setDepartments((prev) => prev.filter((x) => x.id !== departmentToDeleteId));
            setDeleteDialogOpen(false);
            setDepartmentToDeleteId(undefined);
        } else {
            console.error(resultAction.payload);
        }
    };

    const onSucces = (department: CompanyDepartmentDetails) => {
        setCreateDialogVisible(false);
        setEditDialogVisible(false);

        setDepartments((prev) => {
            const exists = prev.some((x) => x.id === department.id);

            if (exists) {
                return prev.map((x) => (x.id === department.id ? department : x));
            }

            return [...prev, department].sort((a, b) => a.displayOrder - b.displayOrder);
        });
    };

    const handleHideDialog = () => {
        setCreateDialogVisible(false);
        setEditDialogVisible(false);
        setSelectedDepartment(undefined);
    }


    useEffect(() => {
        setIsLoading(true);
        setLoadingErrorOccured(false);
        setError(undefined);

        dispatch(loadCompanyDepartments())
            .then(unwrapResult)
            .then((data) => {
                setDepartments(data);
            })
            .catch((error: APIError) => {
                console.error(error);
                setError(error);
                setLoadingErrorOccured(true);
            })
            .finally(() => setIsLoading(false));
    }, [dispatch]);

    const columns: GridColDef<CompanyDepartmentDetails>[] = [
        {
            field: "code",
            headerName: l10n.getString("company-department-code", null, "_Kod"),
            width: 90,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => (
                <Box
                    sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                    }}
                >
                    <DepartmentCodeBadge
                        code={params.row.code}
                        color={params.row.color}
                    />
                </Box>
            )
        },
        {
            field: "name",
            headerName: l10n.getString("company-department-name", null, "_Nazwa"),
            width: 250
        },

        {
            field: "email",
            headerName: l10n.getString("email", null, "_Email"),
            flex: 1,
            minWidth: 220
        },
        {
            field: "notificationTargetType",
            headerName: l10n.getString(
                "company-department-notification-target",
                null,
                "_Strategia powiadomień"
            ),
            width: 240,
            renderCell: (params) => (
                <DepartmentNotificationTargetLabel value={params.value} />
            )
        },

        {
            field: "displayOrder",
            headerName: l10n.getString("company-department-display-order", null, "_Kolejność"),
            width: 120,
            align: "center"
        },

        {
            field: "membersCount",
            headerName: l10n.getString("company-department-members-count", null, "_Liczba członków"),
            width: 150,
            align: "center",
            valueGetter: (_, row) => {
                return row.members?.length ?? 0
            }
        },
        {
            field: "isActive",
            headerName: l10n.getString("company-department-is-active", null, "_Aktywny"),
            width: 130,
            renderCell: (params) => (
                <Checkbox
                    checked={!!params.value}
                    disabled
                    sx={{
                        color: params.value ? "success.main" : "grey.400",
                        '&.Mui-checked': {
                            color: "success.main",
                        },
                        '&.Mui-disabled': {
                            color: params.value ? "success.main" : "grey.400",
                        }
                    }}
                />
            )
        },
        {
            field: "actions",
            headerName: l10n.getString("actions", null, "_Akcje"),
            width: 180,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => (
                <Box
                    sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.5
                    }}
                >
                    <Tooltip title={l10n.getString("edit", null, "_Edytuj")}>
                        <IconButton
                            size="small"
                            onClick={() => handleEditClick(params.row as CompanyDepartmentDetails)}
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title={l10n.getString("company-department-members", null, "_members")}>
                        <IconButton
                            size="small"
                            onClick={() => handleMembersClick(params.row)}
                        >
                            <GroupIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title={l10n.getString("company-department-notifications-action", null, "_Notifications")}>
                        <IconButton
                            size="small"
                            onClick={() => setSelectedDepartmentForNotifications(params.row)}
                        >
                            <NotificationsOutlinedIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title={l10n.getString("delete", null, "_Usuń")}>
                        <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteClick(params.row.id)}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            )
        }
    ];

    if (loadingErrorOccured) {
        return <ErrorContainer error={loadingError} />;
    }

    if (isLoading) {
        return <LoadingContainer />;
    }

    return (
        <>
            <TableContainer component={Paper}>
                <DataGrid
                    localeText={getCustomLocaleText()}
                    style={{ minHeight: 100, height: "calc(100vh - 270px)" }}
                    rows={departments}
                    columns={columns}
                />
                <FlexDiv>
                    <AddFabButton onClick={openCreateDialog} />
                </FlexDiv>
            </TableContainer>
            {
                createDialogVisible &&
                <AddEditCompanyDepartmentDialog
                    department={undefined}
                    mode="create"
                    onSuccessfullySaved={onSucces}
                    onCancel={handleHideDialog}
                />
            }
            {
                editDialogVisible && selectedDepartment &&
                <AddEditCompanyDepartmentDialog
                    department={selectedDepartment}
                    mode="edit"
                    onSuccessfullySaved={onSucces}
                    onCancel={handleHideDialog}
                />
            }
            {deleteDialogOpen && (
                <DeleteConfirmationDialog
                    open={deleteDialogOpen}
                    onConfirm={handleDeleteConfirm}
                    onCancel={handleDeleteCancel}
                />
            )}
            {
                membersDialogVisible && selectedDepartmentForMembers &&
                <CompanyDepartmentMembersDialog
                    open={membersDialogVisible}
                    department={selectedDepartmentForMembers}
                    onCancel={handleHideMembersDialog}
                    onSuccessfullySaved={(updatedDepartments) => {
                        setDepartments(updatedDepartments);
                        handleHideMembersDialog();
                    }}
                />
            }
            {selectedDepartmentForNotifications && (
                <CompanyDepartmentNotificationsDialog
                    department={selectedDepartmentForNotifications}
                    onCancel={() => setSelectedDepartmentForNotifications(undefined)}
                    onSuccessfullySaved={(updatedDepartments) => {
                        setDepartments(updatedDepartments);
                        setSelectedDepartmentForNotifications(undefined);
                    }}
                />
            )}
        </>
    );
};